import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { getFirstPermittedPath } from "../config/menuConfig";
import ErrorBanner from "../components/ErrorBanner";
import { validateEmail } from "../shared/utils/validation";
import useAccountBranding from "../shared/hooks/useAccountBranding";
import { LoginMarketingAside } from "../components/LoginMarketingAside";
import PasswordInput from "../components/PasswordInput";
import { safeReturnUrl } from "../shared/utils/safeReturnUrl";
import "./Login.css";

function Login() {
  const [searchParams] = useSearchParams();
  const returnUrl = safeReturnUrl(searchParams.get("returnUrl") || "", "");
  const attendEventId = searchParams.get("attendEventId") || "";
  const [serverMessage, setServerMessage] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, userInfo, loading, hasPermission, hasRole, authError } = useAuth();

  const { accountBranding, brandingLoading } = useAccountBranding();

  // Redirect only after auth is fully ready (user + userInfo loaded). Fixes "first login doesn't work".
  useEffect(() => {
    if (loading || !user || !userInfo) return;
    const fallback = getFirstPermittedPath(hasPermission, {
      isSuperAdmin: hasRole('Super Admin'),
    }) || "/Dashboard";
    let target = returnUrl || fallback;
    target = safeReturnUrl(target, fallback);
    if (attendEventId && target) {
      const sep = target.indexOf("?") >= 0 ? "&" : "?";
      target = `${target}${sep}attendEventId=${encodeURIComponent(attendEventId)}`;
    }
    navigate(target, { replace: true });
  }, [user, userInfo, loading, navigate, returnUrl, attendEventId, hasPermission, hasRole]);

  function validateForm() {
    const emailCheck = validateEmail(email);
    return emailCheck.valid && password.length > 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      setServerMessage(emailCheck.message || "Invalid email.");
      return;
    }
    if (!password.length || submitting) return;

    setSubmitting(true);
    setServerMessage("");

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setServerMessage("Success – signing you in.");
      // Do not navigate here: wait for AuthContext to finish token exchange and set userInfo,
      // then the useEffect above will redirect. This fixes first-time login not working.
    } catch (err) {
      const message =
        err.code === "auth/invalid-credential" || err.code === "auth/wrong-password" || err.code === "auth/invalid-email"
          ? "Invalid email or password. Please try again."
          : err.code === "auth/user-not-found"
          ? "No account found for this email. Check the email address or create an account."
          : err.code === "auth/too-many-requests"
          ? "Too many attempts. Try again later."
          : err.message || "Login failed.";
      setServerMessage("Failure – " + message);
    } finally {
      setSubmitting(false);
    }
  }

  const isSuccess = serverMessage.startsWith("Success");

  return (
    <div className="login-page">
      <div className="login-layout">
        <LoginMarketingAside />

        <div className="login-form-column">
          <header className="login-hero">
            <h2>{brandingLoading ? "Loading…" : accountBranding?.full_name ? `Sign in to ${accountBranding.full_name}` : "Welcome back"}</h2>
            <p>Sign in to your account to continue</p>
          </header>
          <div className="login-card">
            <form onSubmit={handleSubmit}>
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                value={email}
                placeholder="you@example.com"
                required
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
              <label htmlFor="login-password">Password</label>
              <PasswordInput
                id="login-password"
                value={password}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="login-actions">
                <button type="submit" disabled={!validateForm() || submitting}>
                  {submitting ? "Signing in…" : "Sign in"}
                </button>
                <Link
                  className="login-forgot-link"
                  to={email.trim() ? `/ForgotPassword?email=${encodeURIComponent(email.trim())}` : "/ForgotPassword"}
                >
                  Forgot password?
                </Link>
              </div>
            </form>
            {authError && (
              <ErrorBanner
                message={authError}
                onDismiss={() => {}}
              />
            )}
            {authError && user && (
              <p style={{ marginTop: 8, fontSize: 13 }}>
                <button
                  type="button"
                  onClick={() => signOut(auth)}
                  style={{ background: "none", border: "none", color: "#0d6efd", cursor: "pointer", textDecoration: "underline", padding: 0 }}
                >
                  Sign out
                </button>
                {" "}and try again or contact your administrator.
              </p>
            )}
            {serverMessage && !isSuccess && !authError && (
              <ErrorBanner message={serverMessage} onDismiss={() => setServerMessage("")} />
            )}
            {serverMessage && isSuccess && !authError && (
              <p className="login-message success">{serverMessage}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
